import { Box, Card, Group, SimpleGrid, Skeleton, Text, ThemeIcon } from "@mantine/core";
import { IconAlertTriangle, IconCalendarDue, IconCircleCheck, IconReceipt2 } from "@tabler/icons-react";
import { MoneyCell } from "../../../../components/billing/MoneyCell";
import { formatDate } from "../../../../utils/format";

export interface InvoiceKpiValues {
  total: number;
  nextDate: string | null;
  nextAmount: number;
  outstanding: number;
  paidThisYear: number;
  paidCount: number;
}

interface Props {
  values: InvoiceKpiValues;
  currency: string;
  year: number;
  loading: boolean;
}

interface Tile {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  sub: React.ReactNode;
  accent?: "brand" | "red" | "green";
}

export function InvoiceKpis({ values, currency, year, loading }: Props) {
  const clean = values.outstanding <= 0;

  const tiles: Tile[] = [
    {
      icon: <IconReceipt2 size={20} />,
      label: "Нийт нэхэмжлэх",
      value: <Text fw={700} size="xl" style={{ fontVariantNumeric: "tabular-nums" }}>{values.total}</Text>,
      sub: <Text size="xs" c="dimmed">{year} он</Text>
    },
    {
      icon: <IconCalendarDue size={20} />,
      label: "Дараагийн нэхэмжлэх",
      accent: "brand",
      value: (
        <Group gap={4} wrap="nowrap" align="baseline">
          <Text span c="dimmed">~</Text>
          <MoneyCell value={values.nextAmount} currency={currency} fw={700} />
        </Group>
      ),
      sub: <Text size="xs" c="dimmed">{values.nextDate ? formatDate(values.nextDate) : "Тооцоолсон дүн"}</Text>
    },
    {
      icon: clean ? <IconCircleCheck size={20} /> : <IconAlertTriangle size={20} />,
      label: "Төлөгдөөгүй дүн",
      accent: clean ? "green" : "red",
      value: <MoneyCell value={values.outstanding} currency={currency} fw={700} c={clean ? undefined : "red"} />,
      sub: clean
        ? <Text size="xs" c="green">Цэвэр</Text>
        : <Text size="xs" c="red">Хугацаа хэтэрсэн төлбөртэй</Text>
    },
    {
      icon: <IconCircleCheck size={20} />,
      label: "Энэ онд төлсөн",
      value: <MoneyCell value={values.paidThisYear} currency={currency} fw={700} />,
      sub: <Text size="xs" c="dimmed">{values.paidCount} нэхэмжлэх</Text>
    }
  ];

  return (
    <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
      {tiles.map((t) => (
        <Card key={t.label} withBorder radius="md" padding="md">
          {loading ? (
            <Skeleton height={64} radius="sm" />
          ) : (
            <Group gap="sm" wrap="nowrap" align="flex-start">
              <ThemeIcon variant="light" color={t.accent ?? "gray"} size={40} radius="md">
                {t.icon}
              </ThemeIcon>
              <Box>
                <Text size="xs" c="dimmed">{t.label}</Text>
                {t.value}
                {t.sub}
              </Box>
            </Group>
          )}
        </Card>
      ))}
    </SimpleGrid>
  );
}
